// Statistics utility for Connect4 game
// Handles saving, loading, and updating game statistics in localStorage

import { getCurrentUsername, updateUserStats } from './userManager';

const STATS_STORAGE_KEY = 'connect4-stats';

const defaultStats = {
  pvp: {
    redWins: 0,
    yellowWins: 0,
    draws: 0,
    fastestWin: null, // { winner, time, playerName, username, date }
    slowestWin: null, // { winner, time, playerName, username, date }
    totalGames: 0
  },
  online: {
    wins: 0,
    losses: 0,
    draws: 0,
    totalGames: 0
  },
  pvc: {
    wins: 0,
    losses: 0,
    draws: 0,
    totalGames: 0,
    // Separate high scores for each difficulty level
    easy: {
      wins: 0,
      losses: 0,
      draws: 0,
      topFastestWins: [] // Array of { time, username, date } - top 10 fastest wins
    },
    medium: {
      wins: 0,
      losses: 0,
      draws: 0,
      topFastestWins: [] // Array of { time, username, date } - top 10 fastest wins
    },
    hard: {
      wins: 0,
      losses: 0,
      draws: 0,
      topFastestWins: [] // Array of { time, username, date } - top 10 fastest wins
    },
    topFastestWins: [] // Backward compatibility - will be migrated to difficulty-specific arrays
  }
};

export const loadStats = () => {
  try {
    const savedStats = localStorage.getItem(STATS_STORAGE_KEY);
    if (savedStats) {
      const parsed = JSON.parse(savedStats);
      
      // Migrate old PvC stats to new difficulty-based structure
      if (parsed.pvc && parsed.pvc.topFastestWins && !parsed.pvc.easy) {
        parsed.pvc.easy = { wins: 0, losses: 0, draws: 0, topFastestWins: [] };
        parsed.pvc.medium = { wins: 0, losses: 0, draws: 0, topFastestWins: [] };
        parsed.pvc.hard = { wins: 0, losses: 0, draws: 0, topFastestWins: [] };
        
        // Migrate existing high scores to appropriate difficulty levels
        parsed.pvc.topFastestWins.forEach(record => {
          const difficultyKey = getDifficultyKey(record.difficulty);
          if (difficultyKey && parsed.pvc[difficultyKey]) {
            parsed.pvc[difficultyKey].topFastestWins.push({
              time: record.time,
              username: record.username,
              date: record.date
            });
          }
        });
        
        // Sort and limit each difficulty's high scores to top 10
        ['easy', 'medium', 'hard'].forEach(diff => {
          if (parsed.pvc[diff]) {
            parsed.pvc[diff].topFastestWins.sort((a, b) => a.time - b.time);
            parsed.pvc[diff].topFastestWins = parsed.pvc[diff].topFastestWins.slice(0, 10);
          }
        });
      }
      
      // Merge with default stats to ensure all properties exist
      return {
        pvp: { ...defaultStats.pvp, ...parsed.pvp },
        online: { ...defaultStats.online, ...parsed.online },
        pvc: { 
          ...defaultStats.pvc, 
          ...parsed.pvc,
          easy: { ...defaultStats.pvc.easy, ...parsed.pvc?.easy },
          medium: { ...defaultStats.pvc.medium, ...parsed.pvc?.medium },
          hard: { ...defaultStats.pvc.hard, ...parsed.pvc?.hard }
        }
      };
    }
  } catch (error) {
    console.error('Error loading stats from localStorage:', error);
  }
  return defaultStats;
};

// Helper function to get difficulty key from difficulty value
const getDifficultyKey = (difficulty) => {
  switch (difficulty) {
    case 3: return 'easy';
    case 6: return 'medium';
    case 9: return 'hard';
    default: return null;
  }
};

export const saveStats = (stats) => {
  try {
    localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error('Error saving stats to localStorage:', error);
  }
};

export const updateStatsAfterGame = (gameResult) => {
  const stats = loadStats();
  const { mode, winner, time, difficulty, playerNames } = gameResult;
  const currentUsername = getCurrentUsername();

  // Update user stats (total games played and time spent)
  updateUserStats(time);

  if (mode === 'pvp') {
    // Update PvP stats
    stats.pvp.totalGames++;
    
    if (winner === null) {
      stats.pvp.draws++;
    } else {
      if (winner === 1) { // Player.RED
        stats.pvp.redWins++;
      } else { // Player.YELLOW
        stats.pvp.yellowWins++;
      }

      // Update fastest/slowest win records for PvP
      const winRecord = {
        winner,
        time,
        date: new Date().toISOString(),
        playerName: winner === 1 ? playerNames.red : playerNames.yellow,
        username: currentUsername || 'Unknown Player'
      };

      // Check for fastest win
      if (!stats.pvp.fastestWin || time < stats.pvp.fastestWin.time) {
        stats.pvp.fastestWin = winRecord;
      }

      // Check for slowest win
      if (!stats.pvp.slowestWin || time > stats.pvp.slowestWin.time) {
        stats.pvp.slowestWin = winRecord;
      }
    }
  } else if (mode === 'online') {
    // Update Online PvP stats
    stats.online.totalGames++;
    
    if (winner === null) {
      stats.online.draws++;
    } else if (winner === 1) { // Assuming player is always player 1 in online mode
      stats.online.wins++;
    } else {
      stats.online.losses++;
    }
  } else if (mode === 'pvc') {
    // Update PvC stats
    stats.pvc.totalGames++;
    
    const difficultyKey = getDifficultyKey(difficulty);
    
    if (winner === null) {
      stats.pvc.draws++;
      if (difficultyKey && stats.pvc[difficultyKey]) {
        stats.pvc[difficultyKey].draws++;
      }
    } else if (winner === 1) { // Player wins (red)
      stats.pvc.wins++;
      if (difficultyKey && stats.pvc[difficultyKey]) {
        stats.pvc[difficultyKey].wins++;
      }
      
      // Add to difficulty-specific top fastest wins (high scores) - only for player wins
      const winRecord = {
        time,
        date: new Date().toISOString(),
        username: currentUsername || 'Unknown Player'
      };
      
      if (difficultyKey && stats.pvc[difficultyKey]) {
        stats.pvc[difficultyKey].topFastestWins.push(winRecord);
        
        // Sort by time (fastest first) and keep only top 10 for this difficulty
        stats.pvc[difficultyKey].topFastestWins.sort((a, b) => a.time - b.time);
        stats.pvc[difficultyKey].topFastestWins = stats.pvc[difficultyKey].topFastestWins.slice(0, 10);
      }
      
      // Also maintain the legacy topFastestWins for backward compatibility
      const legacyWinRecord = {
        time,
        difficulty,
        date: new Date().toISOString(),
        username: currentUsername || 'Unknown Player'
      };
      
      stats.pvc.topFastestWins.push(legacyWinRecord);
      stats.pvc.topFastestWins.sort((a, b) => a.time - b.time);
      stats.pvc.topFastestWins = stats.pvc.topFastestWins.slice(0, 10);
      
    } else { // Bot wins (yellow)
      stats.pvc.losses++;
      if (difficultyKey && stats.pvc[difficultyKey]) {
        stats.pvc[difficultyKey].losses++;
      }
    }
  }

  saveStats(stats);
  return stats;
};

export const clearAllStats = () => {
  try {
    localStorage.removeItem(STATS_STORAGE_KEY);
    return defaultStats;
  } catch (error) {
    console.error('Error clearing stats from localStorage:', error);
    return defaultStats;
  }
};

export const getStats = () => {
  return loadStats();
};